import { Component, Input, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SubscribedComponent } from '../subscribed.component';
import { VacancySiblingPosting, MarketVacancy, InterestLevel, MatchingUser, VacancyMatchScore, ScoringFactorDetail, UserAvatarSource } from '../interfaces/market-vacancy';
import { buildConditionView, ConditionView } from './vacancy-conditions';
import { MarketVacancyService } from '../shared/market-vacancy.service';
import { MarketInterestService } from '../shared/market-interest.service';
import { AuthService } from '../shared/auth.service';
import { CvService } from '../cv/cv.service';
import { NbDialogRef, NbDialogService, NbMenuItem, NbPopoverDirective, NbToastrService } from '@nebular/theme';
import { UserDialogComponent } from '../dialogs/popup-dialog/user-dialog.component';
import { CvDocumentsHubDialogComponent } from '../cv/cv-documents-hub/cv-documents-hub-dialog.component';
import { ClientService } from '../shared/client.service';
import { EditDialogComponent, EditType } from '../dialogs/edit-dialog/edit-dialog.component';
import { ColDef, ColType, ListEntry } from '../interfaces/colDef';
import { Globals } from '../shared/Globals';
import { resolveOrgPick } from '../shared/organization-pick.util';
import { TranslateService } from '@ngx-translate/core';
import { DirectoryService } from '../shared/directory.service';
import { MarketNavService } from '../shared/market-nav.service';
import { TitleService } from '../shared/title.service';

type VacancyMutationPayload = { vacancyId: number; hidden?: boolean; interested?: boolean };

@Component({
    selector: 'bst-market-vacancy-detail',
    templateUrl: './market-vacancy-detail.component.html',
    styleUrls: ['./market-vacancy-detail.component.scss'],
    standalone: false
})
export class MarketVacancyDetailComponent extends SubscribedComponent {


  @Input('id') public id: number = null;
  @Input() public embedded: boolean = false;
  @Input() public forUserId?: number;
  @Input() public forUser?: number;

  public vacancy: MarketVacancy = null;
  public conditionView: ConditionView | null = null;
  public showQuotes = false;
  public InterestLevel = InterestLevel;
  public Globals = Globals;
  private closeState: { hidden?: boolean; interested?: boolean } = {};

  // Inline editing
  public editing: boolean = false;
  public filling: boolean = false;
  public privacyExpanded: boolean = false;
  public form: FormGroup;

  // Matching users
  public matchingUsers: MatchingUser[] = [];
  public matchScore: VacancyMatchScore | null = null;
  public matchingUsersLoading: boolean = false;
  public eligibilityPending = false;

  // Per-card "Tailoren (AI)" launcher gate: true when the global tailoring
  // feature flag is on. For a role-1 caller this is additionally AND-gated on
  // the self-serve pilot flag for their tenant. Per-admin/per-client
  // entitlement is enforced server-side in ApplicationPackageController::create
  // (403 on a bad pair).
  public tailoringEnabled: boolean = false;

  constructor(
    private marketVacancyService: MarketVacancyService,
    private marketInterestService: MarketInterestService,
    private clientService: ClientService,
    private route: ActivatedRoute,
    private router: Router,
    public auth: AuthService,
    private translate: TranslateService,
    private dialogService: NbDialogService,
    private fb: FormBuilder,
    private toastr: NbToastrService,
    private cvService: CvService,
    private directoryService: DirectoryService,
    private marketNav: MarketNavService,
    private titles: TitleService,
    @Optional() private dialogRef: NbDialogRef<any>
  ) {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();

    this.form = this.fb.group({
      title: [null],
      company: [null],
      location: [null],
      description: [null],
      url: [null],
      years_experience: [null],
      hours_per_week: [null],
      contract_duration: [null],
      tariff: [null],
      industry: [null],
      posting_date: [null],
      deadline: [null],
      start_date: [null],
      end_date: [null],
      status: [null],
      skills: [[]],
      certifications: [[]],
      developments: [[]],
      industries_list: [[]],
    });

    const forUserId = this.parseId(this.forUserId ?? this.forUser);
    if (forUserId !== null) this.forUserId = forUserId;

    // Gate the per-card Tailor button on the logged-in caller's own
    // entitlement. isSelf=true avoids the cross-client 404; a 404/422 (no
    // active membership) is treated as "feature off". Also keeps the button
    // hidden in prod while the flag is empty (tailoring_client_ids === []).
    const myId = this.currentUserId;
    if ((this.auth.isAdmin || this.auth.isUser) && myId != null) {
      // Pass for: 'tailoring' so this resolves the SAME client create()
      // would use (TailoringClientResolver), not defaults()'s unrelated
      // lowest-sortindex fallback -- otherwise a consultant who also belongs
      // to a non-entitled client with a lower sortindex gets the wrong client
      // evaluated here and the launcher stays hidden even though create()
      // would have succeeded.
      this.subs.push(this.cvService.getDefaults(myId, { for: 'tailoring' }).subscribe({
        next: d => {
          const tailoringOn = (d.tailoring_client_ids?.length ?? 0) > 0;
          // Role-1 users only get the launcher when self-serve is on for their
          // tenant. Privileged users keep the existing behaviour.
          this.tailoringEnabled = this.auth.isUser
            ? tailoringOn && !!d.self_serve_enabled
            : tailoringOn;
        },
        error: (err) => {
          // With the self-serve flag empty (prod/staging today), this call
          // 404s for every role-1 consultant by design -- that is the expected
          // "not eligible" signal, not a failure. Only log genuinely
          // unexpected statuses.
          if (err?.status !== 404) {
            console.error('market-vacancy-detail: tailoring gate check failed', err);
          }
          this.tailoringEnabled = false;
        },
      }));
    }

    const inputId = this.parseId(this.id);
    if (this.embedded) {
      if (inputId !== null) {
        this.id = inputId;
        await this.loadVacancy(inputId);
      }
      return;
    }

    if (inputId !== null) {
      this.id = inputId;
      await this.loadVacancy(inputId);
    } else {
      this.subs.push(this.route.paramMap.subscribe(async (paramMap) => {
        const id = paramMap.get('id');
        if (id) {
          const forUser = this.parseId(this.route.snapshot.queryParamMap.get('for_user'));
          if (forUser !== null) this.forUserId = forUser;
          const vacancyId = this.parseId(id);
          if (vacancyId !== null) await this.loadVacancy(vacancyId);
        }
      }));
    }
  }

  private parseId(value: any): number | null {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  }

  private async loadVacancy(id: number) {
    this.loading.next(true);
    try {
      this.vacancy = await this.marketVacancyService.getMarketVacancy(id, this.forUserId);
      if (!this.vacancy) {
        if (this.embedded && this.dialogRef) {
          this.dialogRef.close(this.closePayload());
        } else {
          await this.router.navigate(['/market/overview']);
        }
      } else {
        this.conditionView = buildConditionView(this.vacancy);
        this.resetForm();
        if (!this.embedded) { await this.announceKind(); }
        // Load matching users after loading vacancy
        this.loadMatchingUsers(id);
      }
    } catch (error) {
      console.error('Failed to load vacancy', error);
      if (this.embedded && this.dialogRef) {
        this.dialogRef.close(this.closePayload());
      } else {
        await this.router.navigate(['/market/overview']);
      }
    } finally {
      this.loading.next(false);
    }
  }

  private eligibilityTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly ELIGIBILITY_POLLS = 8; // 8 x 8s: judging takes 15-30s behind the other lanes

  ngOnDestroy() {
    this.clearEligibilityTimer();
    if (!this.embedded) { this.marketNav.directRequestOpen.next(false); }
    super.ngOnDestroy();
  }

  /** A Direct request lives under Intake: the sidebar highlights it and the header names it. */
  private async announceKind() {
    this.marketNav.directRequestOpen.next(this.isIntake);
    if (this.isIntake) {
      await this.titles.newTitle([await this.translate.get('intake.title').toPromise(), this.vacancy.title]);
    } else {
      await this.titles.newTitle(); // paging from a Direct request to a scraped vacancy reuses this component
    }
  }

  private clearEligibilityTimer() {
    if (this.eligibilityTimer) { clearTimeout(this.eligibilityTimer); this.eligibilityTimer = null; }
  }

  /** poll > 0: a refetch while judging runs; keeps polling until no pair is not_checked or the budget runs out. */
  private async loadMatchingUsers(vacancyId: number, poll = 0) {
    this.clearEligibilityTimer();
    this.matchingUsersLoading = true;
    const unchecked = () => this.matchingUsers.some(m => m.eligibility?.verdict === 'not_checked');
    try {
      const response = await this.marketVacancyService.getMatchingUsers(vacancyId);
      if (!response) { return; }
      this.matchScore = response.match_score;
      this.matchingUsers = response.matching_users;
      if (!poll && unchecked()) {
        this.marketVacancyService.checkEligibility(vacancyId)
          .then(r => {
            this.eligibilityPending = (r?.queued ?? 0) > 0;
            if (this.eligibilityPending) { this.scheduleEligibilityPoll(vacancyId, 1); }
          })
          .catch(() => { /* best effort, silent: the nightly pass judges anyway */ });
      }
    } catch (error) {
      console.error('Failed to load matching users', error);
    } finally {
      this.matchingUsersLoading = false;
      if (poll) {
        // A pair still not_checked after the last poll shows "Eisen (n)" and the admin can reopen.
        this.eligibilityPending = poll < MarketVacancyDetailComponent.ELIGIBILITY_POLLS && unchecked();
        if (this.eligibilityPending) { this.scheduleEligibilityPoll(vacancyId, poll + 1); }
      }
    }
  }

  private scheduleEligibilityPoll(vacancyId: number, poll: number) {
    this.clearEligibilityTimer();
    this.eligibilityTimer = setTimeout(() => this.loadMatchingUsers(vacancyId, poll), 8000);
  }

  public goToUser(userId?: number): void {
    // Guard a malformed avatar payload (missing User_id) from opening a dialog
    // that fetches nothing — a dead/empty modal with no feedback to the user.
    if (!userId) { return; }
    this.dialogService.open(UserDialogComponent, {
      closeOnBackdropClick: false,
      context: {
        componentContext: {
          height: '300px',
          couldEdit: false,
          couldArchive: false,
          id: userId
        }
      }
    });
  }

  public getScoreColor(score: number | null): string {
    if (score === null) return 'basic';
    if (score >= 80) return 'success';
    if (score >= 50) return 'warning';
    if (score > 0) return 'info';
    return 'basic';
  }

  public getUserPicture(match: { user?: UserAvatarSource | null }): string | null {
    if (match.user?.logo?.preview) {
      return Globals.storage(match.user.logo.preview);
    }
    return match.user?.provider_avatar || null;
  }

  public getUserName(match: { user?: UserAvatarSource | null }): string {
    if (!match.user) return 'Unknown User';
    if (match.user.name_first || match.user.name_last) {
      return [match.user.name_first, match.user.name_last].filter(Boolean).join(' ');
    }
    return match.user.username || 'Unknown User';
  }

  /** Names of the colleagues hidden behind the "+N" overflow chip (those past
   *  the first `shown`), comma-joined for the chip's hover tooltip. */
  public teamOverflowNames(shown = 8): string {
    return (this.vacancy?.team_interests ?? []).slice(shown).map(i => this.getUserName(i)).filter(Boolean).join(', ');
  }

  public hasInterest(): boolean {
    return this.vacancy?.interests?.length > 0;
  }

  public hasHidden(): boolean {
    return this.vacancy?.hidden?.length > 0;
  }

  public async toggleInterest() {
    if (!this.vacancy) return;
    this.loading.next(true);
    try {
      const vacancyId = this.vacancy.id;
      const interested = !this.hasInterest();
      if (this.hasInterest()) {
        await this.marketVacancyService.withdrawInterest(vacancyId);
      } else {
        await this.marketVacancyService.expressInterest(vacancyId, this.forUserId);
      }
      this.closeState.interested = interested;
      await this.loadVacancy(vacancyId);
    } catch (error) {
      console.error('Failed to toggle interest', error);
    } finally {
      this.loading.next(false);
    }
  }

  public async toggleHide() {
    if (!this.vacancy) return;
    this.loading.next(true);
    try {
      const vacancyId = this.vacancy.id;
      const hidden = !this.hasHidden();
      if (this.hasHidden()) {
        await this.marketVacancyService.unhideVacancy(vacancyId);
      } else {
        await this.marketVacancyService.hideVacancy(vacancyId);
      }
      this.closeState.hidden = hidden;
      await this.loadVacancy(vacancyId);
    } catch (error) {
      console.error('Failed to toggle hide', error);
    } finally {
      this.loading.next(false);
    }
  }

  public async convertToProject() {
    if (confirm(`Convert "${this.vacancy.title}" to an internal project?`)) {
      try {
        const result = await this.marketVacancyService.convertToProject(this.vacancy.id);
        if (result && result.project) {
          this.router.navigate(['/projects', result.project.id]);
        }
      } catch (error) {
        console.error('Failed to convert vacancy', error);
      }
    }
  }

  /**
   * Review-before-create (doc §6): show the fields that will be copied from
   * the vacancy so the staffer can check and adjust them BEFORE the deal is
   * created. When the vacancy has no linked canonical org (#770), the same
   * dialog also asks for the end client (with inline create).
   */
  public async createDeal(): Promise<void> {
    if (!this.vacancy) return;
    if (this.isIntake) { return this.createIntakeDeal(); }
    const v = this.vacancy;
    const needsClient = !v.Organization_id;

    const defs: ColDef[] = [];
    if (needsClient) {
      defs.push({
        name: 'for_Client_id',
        target: 'organization',
        title: this.translate.instant('crm.create.end_client'),
        action: {
          label: this.translate.instant('crm.create.add_client'),
          handler: async (): Promise<ListEntry | null> => {
            const created = await this.clientService.openCreateClientDialog(this.dialogService);
            return created?.id ? { id: created.id, name: created.name } : null;
          }
        },
        type: ColType.LISTADDER,
        length: 1,
        required: true
      });
    }
    // Non-fatal: a failed catalogue load (already toasted) degrades to an
    // empty picker instead of killing the dialog before it opens.
    const roles = await this.directoryService.getProjectRoles().catch((e) => {
      console.error('project roles load failed', e);
      return [];
    });

    defs.push(
      { name: 'title', title: this.translate.instant('crm.create.title'), type: ColType.STRING, length: 100, required: true },
      // Description matches the Opportunities dialog's: full width, and tall
      // enough for the longest free text on the form. Rate deliberately sets no
      // width; the 100px cap it needed lifting is handled in edit-dialog's own
      // scss, and a width here would make it the one field wider than the rest.
      { name: 'description', title: this.translate.instant('crm.create.description'), type: ColType.AREA, length: 20000, style: { width: '100%', minHeight: '8rem' }, required: false },
      { name: 'rate_target', title: this.translate.instant('crm.rate_indication'), type: ColType.CURRENCY, required: false },
      { name: 'location', title: this.translate.instant('crm.location'), type: ColType.STRING, length: 100, required: false },
      { name: 'submission_deadline', title: this.translate.instant('crm.deadline'), type: ColType.DATE, required: false },
      { name: 'expected_duration', title: this.translate.instant('vacancy.contractduration'), type: ColType.STRING, length: 100, required: false },
      { name: 'skills', target: 'skill', title: this.translate.instant('crm.create.skills'), type: ColType.LISTADDER, required: false },
      { name: 'ProjectRole_id', title: this.translate.instant('crm.create.role'), type: ColType.SELECT,
        list: [{ id: null as any, name: this.translate.instant('crm.none') },
               ...roles.map(r => ({ id: r.id, name: r.name }))], required: false },
    );

    const result = await this.dialogService.open<EditDialogComponent<any>>(EditDialogComponent, {
      closeOnBackdropClick: false,
      context: {
        type: EditType.ADD,
        title: this.translate.instant('crm.opportunity'),
        description: defs,
        data: {
          title: v.title ?? '',
          description: v.description ?? null,
          // tariff is free text from scrapers ('EUR 103/uur'); rate_target is
          // numeric — prefill only a cleanly parsed number, else leave empty.
          rate_target: this.parseTariff(v.tariff),
          location: v.location ?? null,
          submission_deadline: v.deadline ? Globals.fromDateString(v.deadline) : null,
          expected_duration: v.contract_duration ?? null,
          skills: v.skills || [],
        }
      }
    }).onClose.toPromise();
    if (!result) { return; } // cancelled

    // Cleared fields are sent as EXPLICIT null — the review dialog's whole
    // point (§6 "controleren en aanpassen") is that a cleared prefill must not
    // silently revert to the vacancy's value server-side. Title stays
    // undefined-when-empty: it's required in the dialog and NOT NULL in the DB.
    const body: { [k: string]: any } = {
      title: result.title?.trim() || undefined,
      description: result.description?.trim() || null,
      rate_target: result.rate_target ?? null, // ?? not ||: a reviewed rate of 0 is a legitimate value
      location: result.location?.trim() || null,
      submission_deadline: result.submission_deadline ? Globals.toISODate(result.submission_deadline) : null,
      expected_duration: result.expected_duration?.trim() || null,
      skill_ids: (result.skills || []).map((s: any) => s.id),
      ProjectRole_id: result.ProjectRole_id || null,
    };
    if (needsClient) {
      const pick = resolveOrgPick(result.for_Client_id);
      body['for_Client_id'] = pick.clientId;
      body['for_Organization_id'] = pick.organizationId;
    }
    await this.doCreateDeal(body);
  }

  /**
   * Spec 4b: a Direct request reaches the CRM only by marking a consultant
   * Applied, which spawns the opportunity on the linked buyer. The staffer
   * names the consultant here; the page's own consultant skips the picker.
   */
  // The deal is spawned by a queued job, so crm_opportunity_id is still empty right after
  // Applied; this hides the button meanwhile so a second click does not re-send it.
  public dealQueuedFor: number | null = null;

  private async createIntakeDeal(): Promise<void> {
    const v = this.vacancy;
    if (!v.buyer_Client_id) {
      this.toastr.warning(this.translate.instant('intake.detail.link_buyer_first'), 'CRM');
      if (v.intake_mail_id) { void this.router.navigate(['/market/intake', v.intake_mail_id]); }
      return;
    }
    let consultantId = this.forUserId ?? null;
    if (consultantId === null) {
      const result = await this.dialogService.open<EditDialogComponent<any>>(EditDialogComponent, {
        closeOnBackdropClick: false,
        context: {
          type: EditType.ADD,
          title: this.translate.instant('crm.opportunity'),
          description: [{ name: 'consultant', target: 'member', title: this.translate.instant('crm.consultant'),
                          type: ColType.LISTADDER, length: 1, required: true }],
          data: { consultant: [] },
        }
      }).onClose.toPromise();
      consultantId = result?.consultant?.[0]?.id ?? null;
      if (consultantId === null) { return; }
    }
    this.loading.next(true);
    try {
      await this.marketInterestService.setInterest(v.id, InterestLevel.APPLIED, consultantId);
      this.dealQueuedFor = v.id;
      this.toastr.success(this.translate.instant('intake.detail.deal_queued'), 'CRM');
      await this.loadVacancy(v.id);
    } catch (error: any) {
      const key = error?.error?.errors?.level?.[0];
      this.toastr.danger(key ? this.translate.instant(key) : this.translate.instant('crm.err.create_deal'), 'CRM');
    } finally {
      this.loading.next(false);
    }
  }

  /** Execute the opportunity create call and navigate on success. */
  private async doCreateDeal(body: { [k: string]: any } = {}): Promise<void> {
    if (!this.vacancy) return;
    this.loading.next(true);
    try {
      const opp = await this.marketVacancyService.createOpportunity(this.vacancy.id, body);
      // Optimistically mark the vacancy so the button flips without a reload.
      if (opp?.id) {
        this.vacancy = { ...this.vacancy, crm_opportunity_id: opp.id };
        const toastKey = opp.recovered ? 'crm.already_exists_toast' : 'crm.created_toast';
        this.toastr.success(this.translate.instant(toastKey), 'CRM');
        void this.router.navigate(['opportunities', opp.id]);
      }
    } catch (error: any) {
      const msg = error?.error?.message || this.translate.instant('crm.err.create_deal');
      this.toastr.danger(msg, 'CRM');
    } finally {
      this.loading.next(false);
    }
  }

  /** Numeric rate from a free-text tariff ('95', '€ 95,50', 'EUR 103/uur') or null. */
  private parseTariff(tariff: string | number | null | undefined): number | null {
    if (tariff == null) { return null; }
    if (typeof tariff === 'number') { return isFinite(tariff) ? tariff : null; }
    const m = String(tariff).replace(',', '.').match(/\d+(?:\.\d+)?/);
    const n = m ? parseFloat(m[0]) : NaN;
    return isFinite(n) ? n : null;
  }

  /**
   * A sibling posting whose deadline has passed. NULL never expires, matching
   * the server's scopeNotExpired().
   *
   * Day granularity, deliberately: a posting closing today is still open
   * today. Note isExpired() above compares the CURRENT vacancy's deadline
   * against the present instant instead, so it flips at midnight rather than
   * at end of day. Left alone rather than unified - changing it would move a
   * badge users already read, which is not this feature's business.
   */
  public isSiblingExpired(sib: VacancySiblingPosting): boolean {
    if (!sib.deadline) { return false; }
    const d = new Date(sib.deadline);
    if (isNaN(d.getTime())) { return false; }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  }

  public viewDeal(): void {
    if (!this.vacancy?.crm_opportunity_id) return;
    // Close first, like every other navigate-away path here. Without it the
    // deal opens behind a detail dialog that stays up, so the page the user
    // asked for is the one they cannot see. doCreateDeal() navigates the same
    // way and has the same gap, but it is reached only once per vacancy and
    // the toast explains the move; this button exists to go there.
    if (this.dialogRef) { this.dialogRef.close(this.closePayload()); }
    void this.router.navigate(['opportunities', this.vacancy.crm_opportunity_id]);
  }

  public async deleteVacancy() {
    if (confirm(`Are you sure you want to delete "${this.vacancy.title}"?`)) {
      try {
        const result = await this.marketVacancyService.deleteMarketVacancy(this.vacancy.id);
        if (result !== undefined) {
          await this.router.navigate(['/market/overview']);
        }
      } catch (error) {
        console.error('Failed to delete vacancy', error);
        this.toastr.danger('Failed to delete vacancy. Please try again.', 'Error');
      }
    }
  }

  private resetForm(): void {
    this.form.setValue({
      title: this.vacancy.title || null,
      company: this.vacancy.company || null,
      location: this.vacancy.location || null,
      description: this.vacancy.description || null,
      url: this.vacancy.url || null,
      years_experience: this.vacancy.years_experience || null,
      hours_per_week: this.vacancy.hours_per_week || null,
      contract_duration: this.vacancy.contract_duration || null,
      tariff: this.vacancy.tariff || null,
      industry: this.vacancy.industry || null,
      posting_date: this.vacancy.posting_date ? Globals.fromDateString(this.vacancy.posting_date) : null,
      deadline: this.vacancy.deadline ? Globals.fromDateString(this.vacancy.deadline) : null,
      start_date: this.vacancy.start_date ? Globals.fromDateString(this.vacancy.start_date) : null,
      end_date: this.vacancy.end_date ? Globals.fromDateString(this.vacancy.end_date) : null,
      status: this.vacancy.status,
      skills: this.vacancy.skills || [],
      certifications: this.vacancy.certifications || [],
      developments: this.vacancy.developments || [],
      industries_list: this.vacancy.industries || [],
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  public editVacancy(): void {
    this.editing = true;
  }

  public cancelEdit(): void {
    this.resetForm();
    this.editing = false;
  }

  public async fillWithAI(): Promise<void> {
    if (this.filling) return;
    this.filling = true;
    try {
      const suggestions = await this.marketVacancyService.suggestFromDescription(
        this.form.get('title')?.value,
        this.form.get('description')?.value,
        this.form.get('company')?.value
      );
      if (suggestions) {
        const merge = (control: any, items: any[]) => {
          const current = control?.value || [];
          const currentIds = new Set(current.map((x: any) => x.id));
          control?.setValue([...current, ...items.filter((x: any) => !currentIds.has(x.id))]);
        };
        if (suggestions.skills?.length) merge(this.form.get('skills'), suggestions.skills);
        if (suggestions.certifications?.length) merge(this.form.get('certifications'), suggestions.certifications);
        if (suggestions.developments?.length) merge(this.form.get('developments'), suggestions.developments);
        if (suggestions.industries?.length) merge(this.form.get('industries_list'), suggestions.industries);
        if (suggestions.years_experience && !this.form.get('years_experience')?.value) {
          this.form.get('years_experience')?.setValue(suggestions.years_experience);
        }
        if (suggestions.hours_per_week && !this.form.get('hours_per_week')?.value) {
          this.form.get('hours_per_week')?.setValue(suggestions.hours_per_week);
        }
      }
    } catch (error: any) {
      console.error('Failed to get AI suggestions', error);
      const message = error?.error?.message || 'Could not get AI suggestions. Please try again.';
      this.toastr.warning(message, 'AI Fill');
    } finally {
      this.filling = false;
    }
  }

  public async saveVacancy(): Promise<void> {
    try {
      const v = this.form.value;
      const updates: any = {
        title: v.title,
        company: v.company,
        location: v.location,
        description: v.description,
        url: v.url,
        years_experience: v.years_experience,
        hours_per_week: v.hours_per_week,
        contract_duration: v.contract_duration,
        tariff: v.tariff,
        industry: v.industry,
        posting_date: v.posting_date ? Globals.toISODate(v.posting_date) : null,
        deadline: v.deadline ? Globals.toISODate(v.deadline) : null,
        start_date: v.start_date ? Globals.toISODate(v.start_date) : null,
        end_date: v.end_date ? Globals.toISODate(v.end_date) : null,
        status: v.status,
        skill_ids: (v.skills || []).map((s: any) => s.id),
        certification_ids: (v.certifications || []).map((c: any) => c.id),
        development_ids: (v.developments || []).map((d: any) => d.id),
        industry_ids: (v.industries_list || []).map((i: any) => i.id),
      };
      this.vacancy = await this.marketVacancyService.saveMarketVacancy(this.vacancy.id, updates);
      this.conditionView = buildConditionView(this.vacancy);
      this.resetForm();
      this.editing = false;
    } catch (error: any) {
      console.error('Failed to save vacancy', error);
      const message = error?.error?.message || 'Failed to save vacancy. Please try again.';
      this.toastr.danger(message, 'Error');
    }
  }

  // Global vacancies (Client_id null) are shared across all tenants, so only a
  // platform superuser may edit them; tenant admins may only edit vacancies
  // belonging to their own client. Mirrors MarketVacancyPolicy::update().
  public get canEdit(): boolean {
    return this.vacancy?.Client_id != null ? this.auth.isAdmin : this.auth.isSuperuser;
  }

  // Global vacancies (Client_id null) are shared across all tenants, so only a
  // platform superuser may delete them; tenant admins may only delete
  // vacancies belonging to their own client. Mirrors MarketVacancyPolicy::delete().
  public get canDelete(): boolean {
    return this.vacancy?.Client_id != null ? this.auth.isAdmin : this.auth.isSuperuser;
  }

  // createDocuments() closes this dialog first, then opens the hub via the root
  // NbDialogService (root-provided, safe). Do NOT navigate the hub stacked over
  // this still-open dialog: that re-creates the #503 backdrop stranding.
  // userId pre-fills the hub to a matched consultant (per-card launcher); the
  // bar launcher passes none so the user picks the consultant in the hub.
  public createDocuments(userId?: number, event?: Event): void {
    event?.stopPropagation();
    // A role-1 caller is always their own subject (self-serve): force their
    // own id so the hub never presents a consultant picker to a non-privileged
    // user. Privileged callers keep passing whatever userId they were given
    // (undefined for the bar launcher, a matched consultant for the per-card one).
    const subjectId = this.auth.isUser ? (this.currentUserId ?? undefined) : userId;
    // Close this detail whenever it is a dialog (regardless of embedded) so the
    // hub never stacks over it (#503). Inline (non-dialog) hosts have no dialogRef.
    if (this.dialogRef) { this.dialogRef.close(this.closePayload()); }
    this.dialogService.open(CvDocumentsHubDialogComponent, {
      context: { vacancyId: this.vacancy?.id ?? null, userId: subjectId ?? undefined },
      closeOnBackdropClick: false, closeOnEsc: true, hasBackdrop: true,
    });
  }

  // Bar overflow (kebab). Mirrors the @if gates the moved buttons carried:
  // convert = canManage, edit = canEdit, delete = canDelete, hide/unhide always shown.
  public get barMenu(): NbMenuItem[] {
    const items: NbMenuItem[] = [];
    if (this.canManage) { items.push({ title: this.translate.instant('Convert to project'), data: { action: 'convert' } }); }
    if (this.canEdit)   { items.push({ title: this.translate.instant('edit'), data: { action: 'edit' } }); }
    items.push({ title: this.translate.instant(this.hasHidden() ? 'vacancy.unhide' : 'vacancy.hide'), data: { action: 'hide' } });
    if (this.canDelete) { items.push({ title: this.translate.instant('Delete'), data: { action: 'delete' } }); }
    return items;
  }

  @ViewChild('barPop', { read: NbPopoverDirective }) barPop: NbPopoverDirective;

  public runBarAction(action: string): void {
    this.barPop?.hide();
    if (action === 'convert') { void this.convertToProject(); }
    else if (action === 'edit') { this.editVacancy(); }
    else if (action === 'hide') { void this.toggleHide(); }
    else if (action === 'delete') { void this.deleteVacancy(); }
  }

  /**
   * Id of the currently authenticated user, or null if not yet bootstrapped.
   * Used as the subject user id for the inline CV generate panel.
   */
  public get currentUserId(): number | null {
    return this.auth.user.getValue()?.id ?? null;
  }

  public closePayload(): VacancyMutationPayload | null {
    const vacancyId = this.vacancy?.id ?? this.parseId(this.id);
    return vacancyId !== null ? { vacancyId, ...this.closeState } : null;
  }

  public goBack() {
    if (this.embedded && this.dialogRef) {
      this.dialogRef.close(this.closePayload());
      return;
    }
    const from = this.route.snapshot.queryParamMap.get('from');
    if (this.isInternalPath(from)) {
      this.router.navigateByUrl(from);
      return;
    }
    this.router.navigate([this.isIntake ? '/market/intake' : '/market/overview']);
  }

  /**
   * A `from` value is only followed when it is a path within this app.
   *
   * A URL rather than an id, so any page that opens a vacancy can say where
   * back should land without a lookup table here. That generality is why the
   * value is checked: '//example.com' is protocol-relative and would leave the
   * app, and anything not starting with a single slash is not a route.
   */
  private isInternalPath(from: string | null): from is string {
    return !!from && from.startsWith('/') && !from.startsWith('//');
  }

  /** Host of the link, shown next to a Direct request's link so the reader sees where it goes before clicking. */
  public get linkHost(): string | null {
    try {
      return this.vacancy?.url ? new URL(this.vacancy.url).hostname : null;
    } catch {
      return null;
    }
  }

  public openUrl() {
    if (this.vacancy.url) {
      try {
        const parsed = new URL(this.vacancy.url);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
          window.open(this.vacancy.url, '_blank', 'noopener,noreferrer');
        }
      } catch {
        // Invalid URL, do nothing
      }
    }
  }

  public isExpired(): boolean {
    if (!this.vacancy || !this.vacancy.deadline) return false;
    return new Date(this.vacancy.deadline) < new Date();
  }

public get canManage(): boolean {
    return this.auth.isAdmin || this.auth.isStaffer;
  }

  public get isIntake(): boolean {
    return this.vacancy?.source === 'intake';
  }

  /** CRM create-deal action also requires the CRM flag (parity with the opportunity rail). */
  public get canCreateDeal(): boolean {
    return this.canManage && !!this.auth.user.getValue()?.crm_enabled;
  }

  public get headerTitle(): string {
    return null;
  }

  public getUserFactors(match: MatchingUser): { key: string; label: string; score: number; applicable: boolean; notes: string }[] {
    const details = match.match_details;
    if (!details?.scoring_factors) return [];

    // Display order follows the weights in config/matching.php (most important
    // first). Keep in sync if those weights are retuned (#1120).
    const factorOrder = ['certifications', 'hard_skills', 'title_match', 'experience', 'soft_skills', 'industry', 'availability'];

    return Object.entries(details.scoring_factors)
      .filter(([key, factor]: [string, ScoringFactorDetail]) => factor.applicable && factorOrder.includes(key))
      .map(([key, factor]: [string, ScoringFactorDetail]) => ({
        key,
        label: this.translate.instant(`scoring.factors.${key}`) || key,
        score: factor.score || 0,
        applicable: factor.applicable,
        notes: factor.notes || '',
      }))
      .sort((a, b) => factorOrder.indexOf(a.key) - factorOrder.indexOf(b.key));
  }

  public getFactorBarColor(score: number): string {
    if (score > 0) return 'primary';
    return 'basic';
  }

}
